import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import InfoDialog from '../../../common/components/info-dialog/InfoDialog';
import {
    getDevicesDataInit,
    updateDevicesDataInit,
    deleteDevicesDataInit,
    toggleDevicesMonitoringStatusInit,
    clearUpdateDevicesData,
    clearDeleteDevicesData,
    clearToggleDevicesMonitoringStatus
} from '../redux/actions';
import '../supplyChainStyle.scss';
import SearchDCInput from '../../search-dc/SearchDCInput';
import { isValidIpAddress, isValidEmailList } from '../../../common/Utils/validators';


const DeviceDetailsTable = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);
    const deviceTableRef = useRef(null);

    const [tableFilters, setTableFilters] = useState(null);
    const [infoDialogData, setInfoDialogData] = useState(null);

    const selectedDC = useSelector(state => state.searchDC.selectedDCList);

    const {
        devices,
        error,
        updateSuccess, updateError,
        deleteSuccess, deleteError,
        toggleSuccess, toggleError
    } = useSelector((state) => state.supplyChain.device, shallowEqual);

    const tableData = useMemo(() =>
        (devices || [])
            .map(device => ({
                ...device,
                isActive: device.isActive === 'Y' ? 'Active' : 'Inactive'
            }))
            .sort((a, b) => a.deviceId - b.deviceId),
    [devices]);

    const fetchDeviceTableData = useCallback(() => {
        const storeCode = selectedDC?.map(dc => dc.code).join(',');
        if (storeCode) dispatch(getDevicesDataInit(storeCode));
    }, [selectedDC, dispatch]);

    useEffect(() => {
        initializeTableFilters();
        fetchDeviceTableData()
    },[]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error, life: 3000 });
        }
    }, [error]);

    useEffect(() => {
        if (updateSuccess) {
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Device updated successfully', life: 3000 });
            fetchDeviceTableData();
            dispatch(clearUpdateDevicesData());
        } else if (updateError) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: updateError, life: 3000 });
            dispatch(clearUpdateDevicesData());
        }
    }, [updateSuccess, updateError, fetchDeviceTableData]);

    useEffect(() => {
        if (deleteSuccess) {
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Device deleted successfully', life: 3000 });
            fetchDeviceTableData();
            dispatch(clearDeleteDevicesData());
        } else if (deleteError) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: deleteError, life: 3000 });
            dispatch(clearDeleteDevicesData());
        }
    }, [deleteSuccess, deleteError, fetchDeviceTableData]);

    useEffect(() => {
        if (toggleSuccess) {
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Device status toggled successfully', life: 3000 });
            fetchDeviceTableData();
            dispatch(clearToggleDevicesMonitoringStatus());
        } else if (toggleError) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: toggleError, life: 3000 });
            dispatch(clearToggleDevicesMonitoringStatus());
        }
    }, [toggleSuccess, toggleError, fetchDeviceTableData]);

    const initializeTableFilters = () => {
        setTableFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            storeCode: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            deviceName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            ipAddress: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            assetLocation: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            emailAddress: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            isActive: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] }
        });
    };

    const rowEditValidator = (data) => {
        const { deviceName, ipAddress, emailAddress } = data;

        if (!deviceName?.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Validation', detail: 'Device Name is required', life: 3000 });
            return false;
        }

        if (!isValidIpAddress(ipAddress)) {
            toast.current.show({ severity: 'warn', summary: 'Validation', detail: 'Enter a valid IP address (e.g. 10.0.0.1)', life: 3000 });
            return false;
        }

        if (!isValidEmailList(emailAddress)) {
            toast.current.show({ severity: 'warn', summary: 'Validation', detail: 'Enter valid email(s), semicolon-separated', life: 3000 });
            return false;
        }

        return true;
    };

    const onRowEditComplete = (e) => {
        dispatch(updateDevicesDataInit(e.newData));
    };


    // --- Cell editors ---

    const textEditor = (options) => (
        <InputText
            value={options.value}
            onChange={(e) => options.editorCallback(e.target.value)}
            placeholder="Enter value"
        />
    );



    const infoBodyTemplate = (rowData) => (
        <i className="pi pi-info-circle info-icon" onClick={() => setInfoDialogData(rowData)} />
    );

    const monitoringStatusBodyTemplate = (rowData) => {
        const isActive = rowData.isActive === 'Active';
        return (
            <span className={`ping-badge ${isActive ? 'ping-badge--online' : 'ping-badge--down'}`}>
                <i className={`pi ${isActive ? 'pi-check-circle' : 'pi-times-circle'}`} />
                {isActive ? 'Active' : 'Inactive'}
            </span>
        );
    };

    const actionsBodyTemplate = (rowData, options) => {
        const { editing, onInitClick, onSaveClick, onCancelClick } = options.rowEditor;
        const isActive = rowData.isActive === 'Active';

        if (editing) {
            return (
                <div className="row-actions">
                    <Button
                        icon="pi pi-check"
                        className="p-button-text p-button-success p-button-sm"
                        onClick={onSaveClick}
                        tooltip="Save"
                        tooltipOptions={{ position: 'top' }}
                    />
                    <Button
                        icon="pi pi-times"
                        className="p-button-text p-button-secondary p-button-sm"
                        onClick={onCancelClick}
                        tooltip="Cancel"
                        tooltipOptions={{ position: 'top' }}
                    />
                </div>
            );
        }

        return (
            <div className="row-actions">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-text p-button-sm"
                    onClick={onInitClick}
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-danger p-button-sm"
                    onClick={(event) =>
                        confirmPopup({
                            target: event.currentTarget,
                            icon: 'pi pi-info-circle',
                            message: `Delete device ${rowData.deviceName}?`,
                            accept: () => dispatch(deleteDevicesDataInit(rowData.deviceId))
                        })
                    }
                    tooltip="Delete"
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    icon="pi pi-power-off"
                    className={`p-button-text p-button-sm ${isActive ? 'toggle-btn--active' : 'toggle-btn--inactive'}`}
                    onClick={() => dispatch(toggleDevicesMonitoringStatusInit(rowData.deviceId))}
                    tooltip={isActive ? 'Disable monitoring' : 'Enable monitoring'}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    return (
        <div className="supply-chain-container">
			<p className="page-label">
				Supply Chain
				<i className="pi pi-angle-right" />
				Devices
			</p>
			<SearchDCInput
                handleClick={fetchDeviceTableData}
            />
            <div>
            <ConfirmPopup />
            <Toast ref={toast} position="bottom-center" />
            <InfoDialog data={infoDialogData} onClose={() => setInfoDialogData(null)} />
            <div className="d-flex justify-content-end">
                <Button
                    className="p-button-text"
                    icon="pi pi-refresh"
                    onClick={fetchDeviceTableData}
                />
                <Button
                    className="p-button-text"
                    icon="pi pi-download"
                    onClick={() => deviceTableRef.current.exportCSV()}
                />
            </div>
            <DataTable
                value={tableData}
                ref={deviceTableRef}
                showGridlines
                filters={tableFilters}
                filterDisplay="menu"
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                editMode="row"
                rowEditValidator={rowEditValidator}
                onRowEditComplete={onRowEditComplete}
                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                emptyMessage="No devices found"
            >
                <Column field="storeCode" header="DC Name"sortable filter />
                <Column field="deviceName" header="Device Name" editor={(options) => textEditor(options)} sortable filter />
                <Column field="ipAddress" header="IP Address" editor={(options) => textEditor(options)} sortable filter />
                <Column field="assetLocation" header="Asset Location" editor={(options) => textEditor(options)} sortable filter />
                <Column field="emailAddress" header="Email Address" editor={(options) => textEditor(options)} sortable filter />
                <Column field="isActive" header="Monitoring Status" body={monitoringStatusBodyTemplate} sortable filter />
                <Column header="Info" body={infoBodyTemplate} />
                <Column rowEditor header="Actions" body={actionsBodyTemplate} bodyStyle={{ minWidth: '10rem' }} />
            </DataTable>
       </div>
        </div>
    );
};

export default DeviceDetailsTable;
